import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  TextField,
  Typography,
} from "@mui/material";

const MONTH_OPTIONS = [
  { month: 1, label: "January" },
  { month: 2, label: "February" },
  { month: 3, label: "March" },
  { month: 4, label: "April" },
  { month: 5, label: "May" },
  { month: 6, label: "June" },
  { month: 7, label: "July" },
  { month: 8, label: "August" },
  { month: 9, label: "September" },
  { month: 10, label: "October" },
  { month: 11, label: "November" },
  { month: 12, label: "December" },
];

const getCurrentFiscalYear = () => {
  const now = new Date();
  const startYear = now.getUTCMonth() >= 7 ? now.getUTCFullYear() : now.getUTCFullYear() - 1;
  return `${startYear}-${startYear + 1}`;
};

const isValidFiscalYear = (value) => {
  const normalized = String(value || "").trim();
  const match = normalized.match(/^(\d{4})-(\d{4})$/);
  if (!match) return false;
  return Number(match[2]) === Number(match[1]) + 1;
};

const normalizeMonths = (rows = []) => {
  const byMonth = new Map(
    Array.isArray(rows)
      ? rows
        .map((row) => [Number(row?.month), Number(row?.budgetAmount ?? 0)])
        .filter(([month]) => Number.isInteger(month) && month >= 1 && month <= 12)
      : []
  );

  return MONTH_OPTIONS.map(({ month, label }) => ({
    month,
    label,
    budgetAmount: byMonth.has(month) ? String(byMonth.get(month)) : "0",
  }));
};

const formatCurrency = (value) => `$${new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
}).format(Number(value || 0))}`;

export default function SalesBudgetsTab({ instanceUniqueId, canManage = false }) {
  const SERVER = process.env.REACT_APP_NODEJS_SERVER_URL;
  const headers = { headers: { accessToken: localStorage.getItem("accessToken") } };

  const [fiscalYear, setFiscalYear] = useState(getCurrentFiscalYear());
  const [months, setMonths] = useState(() => normalizeMonths());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const annualTotal = useMemo(() => Number(months.reduce((sum, row) => {
    const value = Number(row?.budgetAmount ?? 0);
    return sum + (Number.isFinite(value) ? value : 0);
  }, 0).toFixed(2)), [months]);

  const loadBudgets = async (targetFiscalYear = fiscalYear) => {
    if (!instanceUniqueId || !isValidFiscalYear(targetFiscalYear)) return;
    setLoading(true);
    setError("");
    setSuccessMessage("");
    try {
      const res = await axios.get(
        `${SERVER}/api/instances/${instanceUniqueId}/sales-budgets`,
        {
          ...headers,
          params: { fiscalYear: targetFiscalYear },
        }
      );
      setMonths(normalizeMonths(res.data?.months));
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load sales budgets.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBudgets(fiscalYear);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceUniqueId, fiscalYear]);

  const handleMonthChange = (month, value) => {
    setMonths((previous) => previous.map((entry) => (
      entry.month === month ? { ...entry, budgetAmount: value } : entry
    )));
  };

  const save = async () => {
    if (!canManage || !instanceUniqueId) return;
    if (!isValidFiscalYear(fiscalYear)) {
      setError("Fiscal year must use the YYYY-YYYY format with consecutive years.");
      return;
    }

    const payloadMonths = [];
    for (const row of months) {
      const budgetAmount = Number(row?.budgetAmount ?? 0);
      if (!Number.isFinite(budgetAmount) || budgetAmount < 0) {
        setError(`Enter a non-negative amount for ${row.label}.`);
        return;
      }
      payloadMonths.push({
        month: row.month,
        budgetAmount: Number(budgetAmount.toFixed(2)),
      });
    }

    setSaving(true);
    setError("");
    setSuccessMessage("");
    try {
      const res = await axios.put(
        `${SERVER}/api/instances/${instanceUniqueId}/sales-budgets`,
        {
          fiscalYear,
          months: payloadMonths,
        },
        headers
      );
      setMonths(normalizeMonths(res.data?.months));
      setSuccessMessage(res.data?.message || "Sales budgets saved.");
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to save sales budgets.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 2, display: "grid", gap: 2 }}>
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>
          Sales Budgets
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.8 }}>
          Set company-level monthly sales targets used by reports and budget comparisons.
        </Typography>
      </Box>

      {!canManage ? (
        <Alert severity="warning">
          Sales Budgets settings are available to administrators, finance users, and management roles.
        </Alert>
      ) : null}

      <Paper variant="outlined" sx={{ p: 2, display: "grid", gap: 2 }}>
        <TextField
          label="Fiscal Year"
          value={fiscalYear}
          onChange={(e) => setFiscalYear(e.target.value)}
          size="small"
          sx={{ maxWidth: 220 }}
          helperText="Example: 2026-2027"
          error={Boolean(fiscalYear) && !isValidFiscalYear(fiscalYear)}
        />

        {loading ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 3 }}>
            <CircularProgress size={20} />
            <Typography variant="body2">Loading monthly budgets...</Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(220px, 1fr))" },
              gap: 2,
            }}
          >
            {months.map((row) => (
              <TextField
                key={row.month}
                label={row.label}
                type="number"
                size="small"
                value={row.budgetAmount}
                onChange={(e) => handleMonthChange(row.month, e.target.value)}
                disabled={!canManage}
                inputProps={{ min: 0, step: "0.01" }}
              />
            ))}
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1.5,
            alignItems: "center",
            justifyContent: "space-between",
            pt: 1,
            borderTop: "1px solid #e5e7eb",
          }}
        >
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Annual Budget Total
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              {formatCurrency(annualTotal)}
            </Typography>
          </Box>

          <Button
            variant="contained"
            onClick={save}
            disabled={!canManage || saving || loading || !isValidFiscalYear(fiscalYear)}
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </Box>
      </Paper>

      {successMessage ? <Alert severity="success">{successMessage}</Alert> : null}
      {error ? <Alert severity="error">{error}</Alert> : null}
    </Box>
  );
}
